import React, { useContext, useEffect, useSyncExternalStore } from 'react';
import { AuthContext } from 'context/AuthContext';
import {
  abandonar,
  getEstado,
  registrarSincronizadorDeSessao,
  submeterSenha,
  subscribe,
} from 'services/main/reauthStore';
import ReauthModal from './index';

/* ===== Ponte store → React =================================================
 *
 * `useSyncExternalStore` e não Context: quem MANDA abrir esta modal é um interceptor
 * de axios (`refreshAuthLogic`), que roda fora da árvore de componentes e não tem
 * como chamar um setState. O store é a única coisa que os dois lados enxergam.
 *
 * `getEstado` devolve sempre a MESMA referência enquanto nada muda — requisito do
 * `useSyncExternalStore`, que compara por identidade e entraria em laço de render
 * infinito com um objeto novo a cada chamada.
 *
 * Montado uma vez, no topo da aplicação: a modal precisa aparecer em qualquer rota
 * (menos as públicas, que o store já recusa antes de chegar aqui).
 * ========================================================================== */

export const ReauthModalHost: React.FC = () => {
  const estado = useSyncExternalStore(subscribe, getEstado);
  const { setAuthContext } = useContext(AuthContext) as { setAuthContext: (fn: (prev: any) => any) => void };

  /* Sincroniza o AuthContext do React depois do re-login — é o que dispensa o reload.
   *
   * REGISTRADO no store em vez de reagir à mudança de estado: o contexto precisa estar
   * atualizado ANTES de a fila destravar, e um efeito reagindo ao estado rodaria depois
   * do `resolve()`. O store chama isto de dentro de `aplicarSessao`, na ordem certa.
   *
   * Atualização MÍNIMA: só os campos de sessão, preservando `user` e o resto. */
  useEffect(
    () =>
      registrarSincronizadorDeSessao(({ token, refreshToken, expiresIn }) => {
        setAuthContext(prev => ({ ...prev, isAuthenticated: true, statusCode: true, token, refreshToken, expiresIn }));
      }),
    [setAuthContext],
  );

  if (!estado.ativo) return null;

  return (
    <ReauthModal
      nomeExibicao={estado.nomeExibicao}
      login={estado.login}
      tentativasRestantes={estado.tentativasRestantes}
      erro={estado.erro}
      enviando={estado.enviando}
      sessaoAtivaEmOutroLugar={estado.sessaoAtivaEmOutroLugar}
      onSubmit={(senha, opcoes) => {
        // O resultado é irrelevante aqui: sucesso fecha a modal pelo próprio store
        // (que também resolve a fila), e falha atualiza erro/tentativas. O `catch` existe
        // só para uma rejeição inesperada não virar unhandled rejection.
        void submeterSenha(senha, opcoes).catch(() => undefined);
      }}
      onOutraConta={() => abandonar('outra conta')}
    />
  );
};

export default ReauthModalHost;
